import { Schema } from "effect";

/**
 * Base webhook response for successful processing
 * All webhook endpoints should return this schema
 */
export const WebhookSuccessResponse = Schema.Struct({
  status: Schema.Literal("ok"),
});
export type WebhookSuccessResponse = typeof WebhookSuccessResponse.Type;

/**
 * Google Pub/Sub push message format
 * Reference: https://cloud.google.com/pubsub/docs/push
 *
 * Note: Pub/Sub sends both camelCase and snake_case versions of fields
 * for compatibility. We only need to validate one version.
 */
export const GooglePubSubMessage = Schema.Struct({
  message: Schema.Struct({
    // Base64-encoded message data
    data: Schema.String,
    // Message ID assigned by Pub/Sub (camelCase and snake_case versions)
    messageId: Schema.String,
    // RFC3339 timestamp (camelCase and snake_case versions)
    publishTime: Schema.String,
  }),
  // Pub/Sub subscription name (format: projects/{project}/subscriptions/{subscription})
  subscription: Schema.String,
});
export type GooglePubSubMessage = typeof GooglePubSubMessage.Type;

/**
 * Gmail-specific webhook payload
 * The actual Gmail notification data is base64-encoded in message.data
 */
export const GmailWebhookPayload = GooglePubSubMessage;
export type GmailWebhookPayload = typeof GmailWebhookPayload.Type;

/**
 * Decoded Gmail webhook data (after base64 decoding message.data)
 * This matches the GmailWebhookData schema in gmail-inbound.ts
 *
 * Example decoded data: {"emailAddress":"info@misslola.com","historyId":212216066}
 */
export const GmailWebhookData = Schema.Struct({
  emailAddress: Schema.String,
  historyId: Schema.String,
});
export type GmailWebhookData = typeof GmailWebhookData.Type;

/**
 * Schema that decodes base64-encoded message data and parses it as GmailWebhookData
 */
const Base64GmailWebhookData = Schema.compose(
  Schema.transform(Schema.String, Schema.String, {
    decode: (base64) => Buffer.from(base64, "base64").toString("utf-8"),
    encode: (str) => Buffer.from(str, "utf-8").toString("base64"),
  }),
  Schema.parseJson(GmailWebhookData),
);

/**
 * Gmail webhook payload with automatic base64 decoding
 * Use this to decode the full Pub/Sub message with automatic data parsing
 */
export const GmailWebhookPayloadWithDecodedData = Schema.Struct({
  message: Schema.Struct({
    data: Base64GmailWebhookData,
    messageId: Schema.String,
    publishTime: Schema.String,
  }),
  subscription: Schema.String,
});
export type GmailWebhookPayloadWithDecodedData =
  typeof GmailWebhookPayloadWithDecodedData.Type;

/**
 * Generic webhook validation token response
 * Used for webhook endpoint verification (e.g., Microsoft Graph validation)
 */
export const WebhookValidationResponse = Schema.Struct({
  validationToken: Schema.String,
});
export type WebhookValidationResponse = typeof WebhookValidationResponse.Type;
